import React, { useCallback, useContext, useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { DataStateContext, DataActionsContext, AuthContext, FilterContext, RoleContext, DivisionLensContext } from "@/App";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import ImportModal from "@/components/ImportModal";
import {
  Package, DollarSign, Truck, Clock,
  ShieldAlert, Download, Upload, ArrowUpDown,
  Trash2, AlertTriangle, Users, Loader2, CheckCircle2, ArrowRight,
} from "lucide-react";
import MonthlyTargetModal from "@/components/MonthlyTargetModal";
import AttentionLightbox from "@/components/dashboard/AttentionLightbox";
import MonthDispatchLightbox from "@/components/dashboard/MonthDispatchLightbox";
import { buildAttentionReportHTML } from "@/components/dashboard/attentionReport";
import { STAGE_COLORS, STAGE_PATHS, PIPELINE_STAGES, ALL_STAGES, formatCurrency, parseDateDash } from "@/components/dashboard/utils";
import { StatValue, PipelineStageCard } from "@/components/dashboard/StatCards";
import MonthlyDispatchesChart from "@/components/dashboard/MonthlyDispatchesChart";
import DueThisMonthCard from "@/components/dashboard/DueThisMonthCard";
import LiveUpdatesCard from "@/components/dashboard/LiveUpdatesCard";
import ActiveValueByClientChart from "@/components/dashboard/ActiveValueByClientChart";
import CompletedItemsSection from "@/components/dashboard/CompletedItemsSection";
import ClientOverviewSection from "@/components/dashboard/ClientOverviewSection";
import TopBlockingReasonsChart from "@/components/dashboard/TopBlockingReasonsChart";
import BlockedHeroWidget from "@/components/dashboard/BlockedHeroWidget";
import { useBlockCatalogue } from "@/lib/blockCatalogue";
import { usePipelineTrend } from "@/contexts/PipelineTrendContext";
import { dashboardIntroPending, markDashboardIntroPlayed } from "@/lib/dashboardIntro";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;


export default function Dashboard() {
  const { dashboardStats, loading, items } = useContext(DataStateContext);
  const { refreshData } = useContext(DataActionsContext);
  const { user } = useContext(AuthContext);
  const { canDo } = useContext(RoleContext);
  const { globalFilter } = useContext(FilterContext);
  const navigate = useNavigate();
  const [reasonCatalogue, setReasonCatalogue] = useState(null);
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/reason-codes`).then(r => setReasonCatalogue(r.data)).catch(() => {});
  }, []);
  // The hero's Blocked panel stands in for the BLOCKED counter only when it can
  // do the counter's job: blocking v2 armed on this server, and this user
  // allowed on the board. Otherwise the counter stays and the hero spans full
  // width — no role sees a half-empty hero.
  const { available: blocksV2 } = useBlockCatalogue();
  const showBlockedWidget = blocksV2 && !!(canDo && canDo("can_view_blocked_board"));

  const [sortBy, setSortBy] = useState("due_date");
  const [showImport, setShowImport] = useState(false);
  const [resetStep, setResetStep] = useState(null); // null | 1 | 2
  const [resetting, setResetting] = useState(false);
  const [attentionMode, setAttentionMode] = useState(null); // null | "blocked" | "overdue"
  const [dispatchLightboxOpen, setDispatchLightboxOpen] = useState(false);
  // Overdue zone: the card's right edge presses in (~0.5s), then the list opens.
  const [odPressing, setOdPressing] = useState(false);
  const pressOverdue = useCallback(() => {
    const noMotion =
      document.documentElement.dataset.flat === "1" ||
      (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
    if (noMotion) { setAttentionMode("overdue"); return; }
    setOdPressing(true);
    setTimeout(() => { setOdPressing(false); setAttentionMode("overdue"); }, 480);
  }, []);

  // Charts animate in only on the first dashboard visit after RockLink opens;
  // revisits (from a stage page, Stores…) draw them straight away.
  const [introAnimate] = useState(dashboardIntroPending);
  useEffect(() => { markDashboardIntroPlayed(); }, []);

  // 7-day trend — fetched once by Layout, shared with the pipeline rail.
  const pipelineTrend = usePipelineTrend();

  // Monthly target — current month
  const [monthlyTarget, setMonthlyTarget] = useState(null);
  // Bumped each time a monthly target is saved — passed to MonthlyDispatchesChart
  // so it re-fetches the FY range and animates a brief refresh sweep across
  // both bar + cumulative panels.
  const [targetVersion, setTargetVersion] = useState(0);
  const [showTargetModal, setShowTargetModal] = useState(false);
  const fetchMonthlyTarget = React.useCallback(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth() + 1;
    const monthName = now.toLocaleString("en-US", { month: "long" });
    const token = localStorage.getItem("rl_token");
    axios.get(`${BACKEND_URL}/api/monthly-targets/${y}/${m}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => {
        const d = r.data || {};
        const total = d.total || 0;
        const dispatched = d.dispatched || 0;
        const hasTarget = total > 0;
        const pct = hasTarget ? Math.min(100, Math.round((dispatched / total) * 100)) : 0;
        setMonthlyTarget({ ...d, monthName, hasTarget, pct });
      })
      .catch(() => setMonthlyTarget({ year: y, month: m, monthName, total: 0, dispatched: 0, hasTarget: false, pct: 0 }));
  }, []);
  useEffect(() => { fetchMonthlyTarget(); }, [fetchMonthlyTarget, items.length]);

  const isAdmin = user?.role === "Admin";
  const showValue = canDo("can_view_value");
  const itemCount = items.length;

  // Target setters — list of non-admin emails allowed to set monthly targets
  const [targetSetters, setTargetSetters]       = useState([]);
  const [showSettersModal, setShowSettersModal] = useState(false);
  const [allUsers, setAllUsers]                 = useState([]);
  const [settersLoading, setSettersLoading]     = useState(false);
  const [settersSaving, setSettersSaving]       = useState(false);
  const [pendingSetters, setPendingSetters]     = useState([]); // editing state inside modal

  const fetchTargetSetters = useCallback(() => {
    const token = localStorage.getItem("rl_token");
    axios.get(`${BACKEND_URL}/api/settings`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(r => setTargetSetters(r.data.target_setters || []))
      .catch(() => {});
  }, []);

  useEffect(() => { fetchTargetSetters(); }, [fetchTargetSetters]);

  const fetchAllUsers = useCallback(() => {
    if (!isAdmin) return;
    const token = localStorage.getItem("rl_token");
    setSettersLoading(true);
    axios.get(`${BACKEND_URL}/api/users`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(r => setAllUsers(r.data || []))
      .catch(() => {})
      .finally(() => setSettersLoading(false));
  }, [isAdmin]);

  // Open setters modal — pre-populate pending list from current settings
  const handleOpenSettersModal = () => {
    fetchAllUsers();
    setPendingSetters([...targetSetters]);
    setShowSettersModal(true);
  };

  const handleSaveSetters = async () => {
    setSettersSaving(true);
    try {
      const token = localStorage.getItem("rl_token");
      await axios.put(
        `${BACKEND_URL}/api/settings`,
        { target_setters: pendingSetters },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      setTargetSetters([...pendingSetters]);
      toast.success("Target setter permissions saved");
      setShowSettersModal(false);
    } catch (e) {
      toast.error("Failed to save", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSettersSaving(false);
    }
  };

  const toggleSetter = (email) => {
    setPendingSetters(prev =>
      prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]
    );
  };

  const canSetTarget = !isAdmin && targetSetters.includes(user?.email);

  // Division lens (Phase 1b): scopes the dashboard through the same
  // client-side stats override the global filter already uses. The lens
  // deliberately does NOT trigger the orange "Filtered View" banner — its
  // indicator is the coloured ring on the pipeline bar.
  const { lens: divisionLens } = useContext(DivisionLensContext) || { lens: "all" };
  const lensActive = divisionLens !== "all";

  // Apply global filters + division lens to items for dashboard views
  const filteredItems = useMemo(() => {
    let f = items;
    if (lensActive) f = f.filter(i => (i.division || "metallic") === divisionLens);
    if (globalFilter.clients) f = f.filter(i => globalFilter.clients.includes(i.client));
    if (globalFilter.priority !== "all") f = f.filter(i => i.priority === globalFilter.priority);
    return f;
  }, [items, globalFilter, divisionLens, lensActive]);

  const isFiltered = globalFilter.clients != null || globalFilter.priority !== "all";
  const statsOverridden = isFiltered || lensActive;

  // Compute stats from filteredItems when a filter or the lens is active
  const filteredStats = useMemo(() => {
    if (!statsOverridden) return null; // use server stats
    // "Active" = not yet out the door. Mirrors the backend _is_shipped helper
    // and isShipped() in MonthlyDispatchesChart.js: an item leaves the active
    // set once it's Completed (delivered) or Dispatch+Shipped/Delivered.
    const isShipped = i =>
      i.stage === "Completed" ||
      (i.stage === "Dispatch" && (i.dispatch_status === "Shipped" || i.dispatch_status === "Delivered"));
    const activeItems = filteredItems.filter(i => !isShipped(i));
    const completedItems = filteredItems.filter(i => isShipped(i));
    const today = new Date(); today.setHours(0,0,0,0);
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const stageStats = {};
    ALL_STAGES.forEach(s => {
      const si = filteredItems.filter(i => i.stage === s);
      stageStats[s] = { count: si.length, value: si.reduce((sum, i) => sum + (i.value_usd || 0), 0) };
    });
    // client_stats: group by client
    const clientStats = {};
    const uniqueClients = [...new Set(filteredItems.map(i => i.client))];
    uniqueClients.forEach(client => {
      const ci = filteredItems.filter(i => i.client === client);
      const today2 = new Date(); today2.setHours(0,0,0,0);
      clientStats[client] = {
        items: ci,
        active_count: ci.filter(i => !isShipped(i)).length,
        shipped_count: ci.filter(i => isShipped(i)).length,
        blocked_count: ci.filter(i => i.is_blocked).length,
        overdue_count: ci.filter(i => {
          const d = parseDateDash(i.dispatch_due_date || i.due_date);
          return d && d < today2 && !isShipped(i);
        }).length,
        total_value: ci.reduce((s, i) => s + (i.value_usd || 0), 0),
      };
    });
    return {
      active_count: activeItems.length,
      active_value: activeItems.reduce((s, i) => s + (i.value_usd || 0), 0),
      shipped_count: completedItems.length,
      blocked_count: filteredItems.filter(i => i.is_blocked).length,
      overdue_count: filteredItems.filter(i => {
        const d = parseDateDash(i.dispatch_due_date || i.due_date);
        return d && d < today && !isShipped(i);
      }).length,
      due_this_month_count: filteredItems.filter(i => {
        const d = parseDateDash(i.dispatch_due_date || i.due_date);
        return d && d >= today && d <= endOfMonth;
      }).length,
      due_this_month_parts: filteredItems.filter(i => {
        const d = parseDateDash(i.dispatch_due_date || i.due_date);
        return d && d >= today && d <= endOfMonth;
      }).reduce((s, i) => s + (i.quantity || 0), 0),
      stage_stats: stageStats,
      client_stats: clientStats,
      active_value_by_client: Object.fromEntries(uniqueClients.map(c => [c, filteredItems.filter(i => i.client === c && !isShipped(i)).reduce((s, i) => s + (i.value_usd || 0), 0)])),
    };
  }, [filteredItems, statsOverridden]);

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('rl_token');
      const res = await fetch(`${BACKEND_URL}/api/line-items/export`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const now = new Date();
      const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
      const filename = `rocklink_export_${String(now.getDate()).padStart(2,"0")}-${months[now.getMonth()]}-${String(now.getFullYear()).slice(-2)}.csv`;
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { console.error("Export failed:", e); }
  };

  const handleResetConfirm = async () => {
    setResetting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/data/reset`);
      setResetStep(null);
      await refreshData();
    } catch (e) {
      console.error("Reset failed:", e);
    } finally {
      setResetting(false);
    }
  };

  if (loading || !dashboardStats) {
    return (<div className="flex items-center justify-center h-64" data-testid="dashboard-loading"><div className="text-[hsl(var(--muted-foreground))]">Loading dashboard...</div></div>);
  }

  const stats = filteredStats || dashboardStats;

  return (
    <div className="" data-testid="dashboard">
      {/* Active Filter Banner */}
      {isFiltered && (
        <div className="flex items-center gap-3 px-4 py-2.5 rounded-lg border border-orange-500/30 bg-orange-500/8 mb-4" data-testid="filter-active-banner">
          <span className="text-xs font-semibold text-orange-400 uppercase tracking-wider">Filtered View</span>
          <div className="flex items-center gap-1.5">
            {globalFilter.clients && <span className="px-2 py-0.5 rounded-full bg-orange-500/15 text-orange-400 text-xs font-medium border border-orange-500/20">{globalFilter.clients.length} clients</span>}
            {globalFilter.priority !== "all" && <span className="px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 text-xs font-medium border border-amber-500/20">{globalFilter.priority}</span>}
          </div>
          <span className="text-[hsl(var(--muted-foreground))] text-xs">{filteredItems.length} of {items.length} items</span>
        </div>
      )}

      {/* ── Hero block: Stats + Pipeline, with the Blocked Board panel beside it ──
          The panel replaces the BLOCKED counter for anyone who can open the
          board; everyone else keeps the counter and the hero spans full width,
          so the layout never shifts between roles. */}
      <div className="flex flex-wrap xl:flex-nowrap items-stretch gap-4 mb-4">
      <div className={`relative flex-1 min-w-0 rounded-xl px-5 py-4 md:p-0 rl-glass rl-hero-glass md:flex md:flex-row md:items-stretch md:overflow-hidden ${odPressing ? "rl-press-right" : ""}`} data-testid="dashboard-hero">
        {/* KPI stacks sit 20px above centre (extra bottom padding) so they line up
            with the Blocked Board card's owner list, which is lifted by its
            footer (Tushar, 3-Nov-2026). Same lift in BlockedHeroWidget. */}
        <div className="md:flex md:items-center md:pl-7 md:pt-5 md:pb-[60px] md:min-w-0">
        {/* Inline stats row — its rule only separates the phone-only Pipeline
            cards below, so it drops away from md up. Active items' stage bar
            hangs BELOW its box (absolute), so every stat is the same height and
            the row lines up with the separate Overdue zone on the right. */}
        <div className="flex items-center md:items-start flex-wrap gap-y-3 pb-3 mb-3 border-b md:pb-0 md:mb-0 md:border-b-0" style={{ borderColor: "hsl(var(--border) / 0.5)", columnGap: 24 }} data-testid="stat-cards">
          <StatValue label="Active Items" value={stats.active_count} testId="stat-active-items" big caption={`across ${PIPELINE_STAGES.length} live stages`}>
            {/* Where the active items sit, in stage colours (rail order). */}
            {(() => {
              const parts = PIPELINE_STAGES.map(st => [st, stats.stage_stats[st]?.count || 0]).filter(([, n]) => n > 0);
              if (!parts.length) return null;
              return (
                <span className="hidden md:flex absolute left-0 top-full gap-[2px] h-[5px] w-[120px] mt-2 rounded-full overflow-hidden" aria-hidden="true" data-testid="stat-active-split">
                  {parts.map(([st, n]) => <i key={st} title={`${st}: ${n}`} style={{ flex: n, background: STAGE_COLORS[st].color }} />)}
                </span>
              );
            })()}
          </StatValue>
          <div className="w-px h-9 md:h-14 shrink-0 bg-[hsl(var(--border)/0.7)]" aria-hidden="true" />
          {showValue && <>
            <StatValue label="Active Value" value={formatCurrency(stats.active_value)} testId="stat-active-value" big caption="open orders, not yet shipped" />
            <div className="w-px h-9 md:h-14 shrink-0 bg-[hsl(var(--border)/0.7)]" aria-hidden="true" />
          </>}
          <StatValue label="Shipped" value={stats.shipped_count} testId="stat-shipped" big caption="shipped and closed, to date" />
          <div className="w-px h-9 md:h-14 shrink-0 bg-[hsl(var(--border)/0.7)]" aria-hidden="true" />
          {!showBlockedWidget && <>
            <button type="button" onClick={() => setAttentionMode("blocked")} data-testid="stat-blocked" title="See the blocked items" className="group rl-stat-btn flex flex-col gap-0.5 text-left cursor-pointer focus-visible:outline-none">
              <span className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-[0.08em] text-[hsl(var(--muted-foreground))]/70">
                <span className="rl-blocked-dot w-1.5 h-1.5 rounded-full shrink-0" style={{ background: "#ef4444" }} aria-hidden="true" />
                Blocked
              </span>
              <span className="flex items-center gap-2 md:mt-1">
              <span className="text-[1.5rem] md:text-[1.75rem] font-bold font-mono-data tracking-tight leading-none" style={{ color: "#ef4444" }}>{stats.blocked_count}</span>
              {/* Hover/focus hint that this number opens the list (3-Nov-2026). */}
              <span className="rl-stat-open red" aria-hidden="true"><span className="rl-stat-word">View</span><ArrowRight className="w-3 h-3" strokeWidth={2.4} /></span>
            </span>
              <span className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1 leading-tight">items on hold</span>
            </button>
            <div className="w-px h-9 md:h-14 shrink-0 bg-[hsl(var(--border)/0.7)]" aria-hidden="true" />
          </>}
        </div>
        </div>
        {/* Overdue — the whole area from its divider to the card's right edge,
            full height, is the button (Tushar, 3-Nov-2026). Hover: a faint
            amber wash from the right + the "View" chip. Click: the card's
            RIGHT EDGE presses in for ~0.5s (.rl-press-right), then the list
            opens. Performance Mode / reduced motion open it straight away. */}
          <button type="button" onClick={pressOverdue} data-testid="stat-overdue" title="See the overdue items" className="group rl-stat-btn rl-od-zone mt-3 md:mt-0 md:flex-1 md:min-w-[196px] min-[1440px]:min-w-[220px] flex flex-col justify-center gap-0.5 md:pl-6 md:pr-7 md:pt-5 md:pb-[60px] text-left cursor-pointer focus-visible:outline-none">
            <span className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-[0.08em] text-[hsl(var(--muted-foreground))]/70">
              <span className="rl-overdue-dot w-1.5 h-1.5 rounded-full shrink-0" style={{ background: "#f59e0b" }} aria-hidden="true" />
              Overdue
            </span>
            <span className="flex items-center gap-2 md:mt-1">
              <span className="text-[1.5rem] md:text-[1.75rem] font-bold font-mono-data tracking-tight leading-none" style={{ color: "#d97706" }}>{stats.overdue_count}</span>
              {/* Hover/focus hint that this number opens the list (3-Nov-2026). */}
              <span className="rl-stat-open amber" aria-hidden="true"><span className="rl-stat-word">View</span><ArrowRight className="w-3 h-3" strokeWidth={2.4} /></span>
            </span>
            <span className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1 leading-tight">past the promised date</span>
          </button>

        <AttentionLightbox
          open={attentionMode !== null}
          onOpenChange={(v) => { if (!v) setAttentionMode(null); }}
          mode={attentionMode || "blocked"}
          items={filteredItems}
        />
        <MonthDispatchLightbox
          open={dispatchLightboxOpen}
          onOpenChange={setDispatchLightboxOpen}
          items={filteredItems}
          monthName={monthlyTarget?.monthName || new Date().toLocaleString("en-US", { month: "long" })}
          year={new Date().getFullYear()}
          month={new Date().getMonth()}
        />

        {/* Pipeline cards — phones only. From md up the header's pipeline
            rail carries the same counts, values and sparklines (header
            redesign, 2-Nov-2026); on phones that rail is hidden. */}
        <div className="md:hidden" data-testid="pipeline-visualization">
          <h2 className="text-[9px] font-semibold uppercase tracking-[0.1em] text-[hsl(var(--muted-foreground))]/60 mb-2">Pipeline</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {(() => {
              const maxStageCount = Math.max(...PIPELINE_STAGES.map(s => stats.stage_stats[s]?.count || 0), 1);
              return PIPELINE_STAGES.map(stage => (
                <PipelineStageCard
                  key={stage}
                  stage={stage}
                  count={stats.stage_stats[stage]?.count || 0}
                  value={stats.stage_stats[stage]?.value || 0}
                  onClick={() => navigate(STAGE_PATHS[stage])}
                  showValue={showValue}
                  trendData={pipelineTrend?.series?.[stage] || []}
                  maxCount={maxStageCount}
                />
              ));
            })()}
          </div>
        </div>
      </div>
      {showBlockedWidget && <BlockedHeroWidget items={filteredItems} />}
      </div>

      {/* Live Updates (left) + Monthly Dispatches (right) — mb-6 before client/blocking section.
          From md up the feed takes the CHART's height (absolute fill, scrolls
          inside) instead of stretching the chart to a fixed 720px (3-Nov-2026). */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch mb-6">
        <div className="relative md:min-h-[420px]">
          <LiveUpdatesCard token={localStorage.getItem("rl_token")} className="max-h-[560px] md:max-h-none md:absolute md:inset-0" />
        </div>
        <div className="md:col-span-2 h-full flex flex-col">
          <MonthlyDispatchesChart
            animate={introAnimate}
            items={filteredItems}
            scrapSales={stats?.scrap_sales}
            targetVersion={targetVersion}
            target={monthlyTarget}
            isAdmin={isAdmin}
            canSetTarget={canSetTarget}
            onSetTarget={() => setShowTargetModal(true)}
            onManageSetters={handleOpenSettersModal}
            onDispatchedClick={() => setDispatchLightboxOpen(true)}
          />
        </div>
      </div>

      <MonthlyTargetModal
        open={showTargetModal}
        onOpenChange={setShowTargetModal}
        items={items}
        showValue={showValue}
        onSaved={() => { fetchMonthlyTarget(); setTargetVersion(v => v + 1); }}
      />

      {/* ── Target Setters Modal (admin only) ── */}
      <Dialog open={showSettersModal} onOpenChange={setShowSettersModal}>
        <DialogContent className="max-w-md w-[95vw]" data-testid="target-setters-modal">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-orange-500/10 shrink-0">
                <Users className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <DialogTitle className="text-base font-bold">Manage Target Setters</DialogTitle>
                <DialogDescription className="text-xs mt-0.5">
                  Select non-admin users who can set and edit monthly targets.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="mt-2 max-h-72 overflow-y-auto space-y-1">
            {settersLoading ? (
              <div className="flex items-center justify-center py-8 text-[hsl(var(--muted-foreground))]">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading users…
              </div>
            ) : allUsers.filter(u => u.role !== "Admin").length === 0 ? (
              <div className="py-6 text-center text-sm text-[hsl(var(--muted-foreground))]">No non-admin users found.</div>
            ) : (
              allUsers
                .filter(u => u.role !== "Admin")
                .map(u => {
                  const checked = pendingSetters.includes(u.email);
                  return (
                    <label
                      key={u.user_id || u.email}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-md cursor-pointer transition-colors ${
                        checked ? "bg-orange-500/8 border border-orange-500/30" : "border border-transparent hover:bg-[hsl(var(--muted))]/40"
                      }`}
                      data-testid={`setter-toggle-${u.email}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleSetter(u.email)}
                        className="w-4 h-4 rounded accent-orange-500 cursor-pointer shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate">{u.name || u.email}</div>
                        <div className="text-[10px] text-[hsl(var(--muted-foreground))] truncate">{u.email} · {u.role}</div>
                      </div>
                      {checked && <CheckCircle2 className="w-4 h-4 text-orange-500 shrink-0" />}
                    </label>
                  );
                })
            )}
          </div>

          <div className="flex items-center justify-between pt-3 border-t border-[hsl(var(--border))]">
            <span className="text-xs text-[hsl(var(--muted-foreground))]">
              {pendingSetters.length} user{pendingSetters.length === 1 ? "" : "s"} can set targets
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setShowSettersModal(false)}
                className="text-xs px-3 py-2 rounded text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]/60 transition-colors"
                data-testid="setters-modal-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveSetters}
                disabled={settersSaving}
                className="text-xs font-bold px-4 py-2 rounded bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50 transition-colors flex items-center gap-1.5"
                data-testid="setters-modal-save"
              >
                {settersSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                Save
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Client Overview + Top Blocking Reasons — side by side, equal height */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch mb-6">
        <ClientOverviewSection
          clientStats={stats.client_stats}
          activeValueByClient={stats.active_value_by_client}
          scrapSales={stats.scrap_sales}
          showValue={showValue}
        />


        {/* Top Blocking Reasons + Attention report */}
        <TopBlockingReasonsChart
          items={filteredItems}
          catalogue={reasonCatalogue}
          onGenerateReport={() => {
            const html = buildAttentionReportHTML({
              items: filteredItems,
              catalogue: reasonCatalogue,
              companyName: "Rockpecker Pvt Ltd",
              reportedBy: user?.name || user?.email || "RockLink user",
            });
            const blob = new Blob([html], { type: "text/html;charset=utf-8" });
            const url = URL.createObjectURL(blob);
            const win = window.open(url, "_blank");
            if (!win) { toast.error("Pop-up blocked — please allow pop-ups to view the report"); }
            setTimeout(() => URL.revokeObjectURL(url), 60 * 1000);
          }}
        />
      </div>

      {/* Completed Items */}
      <CompletedItemsSection items={filteredItems} isAdmin={isAdmin} showValue={showValue} />

      {/* Import / Export / Reset Footer */}
      <div className="flex items-center justify-center gap-3 pt-3 border-t border-[hsl(var(--border))]" data-testid="import-export-footer">
        {canDo("can_import") && (
          <button
            onClick={() => setShowImport(true)}
            className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] px-2.5 py-1.5 rounded rl-glass-hairline rl-edge-slate bg-[hsl(var(--muted))]/40 text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]/80 hover:text-[hsl(var(--foreground))] transition-colors"
            data-testid="import-btn"
          >
            <Upload className="w-3.5 h-3.5" /> Import
          </button>
        )}
        {(isAdmin || canDo("can_export")) && (
          <button
            onClick={handleExport}
            className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] px-2.5 py-1.5 rounded rl-glass-hairline rl-edge-slate bg-[hsl(var(--muted))]/40 text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]/80 hover:text-[hsl(var(--foreground))] transition-colors"
            data-testid="export-btn"
          >
            <Download className="w-3.5 h-3.5" /> Export
          </button>
        )}
        {isAdmin && (
          <button
            onClick={() => setResetStep(1)}
            className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] px-2.5 py-1.5 rounded rl-glass-hairline rl-edge-red bg-red-500/8 text-red-500 hover:bg-red-500/15 transition-colors"
            data-testid="reset-data-btn"
          >
            <Trash2 className="w-3.5 h-3.5" /> Reset Data
          </button>
        )}
      </div>

      {/* Step 1 — First confirmation */}
      <Dialog open={resetStep === 1} onOpenChange={(o) => { if (!o) setResetStep(null); }}>
        <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-md" data-testid="reset-dialog-step1">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-500">
              <AlertTriangle className="w-5 h-5" /> Reset All Data?
            </DialogTitle>
            <DialogDescription className="pt-2 space-y-2">
              <p>You are about to permanently delete <strong className="text-[hsl(var(--foreground))]">{itemCount} line item{itemCount !== 1 ? 's' : ''}</strong> and clear the entire history log.</p>
              <p className="text-amber-500 font-medium">This action cannot be undone.</p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <button
              onClick={() => setResetStep(null)}
              className="px-4 py-2 rounded-md text-sm font-medium bg-[hsl(var(--muted))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]/80 transition-colors"
              data-testid="reset-cancel-step1"
            >
              Cancel
            </button>
            <button
              onClick={() => setResetStep(2)}
              className="px-4 py-2 rounded-md text-sm font-medium bg-amber-500 text-white hover:bg-amber-600 transition-colors"
              data-testid="reset-continue-step1"
            >
              Continue
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Step 2 — Final confirmation */}
      <Dialog open={resetStep === 2} onOpenChange={(o) => { if (!o) setResetStep(null); }}>
        <DialogContent className="bg-[hsl(var(--card))] border-red-500/40 max-w-md" data-testid="reset-dialog-step2">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-500">
              <AlertTriangle className="w-5 h-5" /> FINAL WARNING
            </DialogTitle>
            <DialogDescription className="pt-2 space-y-3">
              <div className="px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm space-y-1">
                <p className="font-bold uppercase tracking-wider text-xs mb-2">You are about to irreversibly erase:</p>
                <ul className="list-disc list-inside space-y-0.5 text-xs">
                  <li>All <strong>{itemCount}</strong> line items across every stage</li>
                  <li>The complete history log</li>
                  <li>All status, block, and QUAL flags</li>
                </ul>
              </div>
              <p className="text-[hsl(var(--muted-foreground))] text-sm">There is no backup. This cannot be recovered.</p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <button
              onClick={() => setResetStep(null)}
              className="px-4 py-2 rounded-md text-sm font-medium bg-[hsl(var(--muted))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]/80 transition-colors"
              data-testid="reset-cancel-step2"
            >
              Cancel — Keep Data
            </button>
            <button
              onClick={handleResetConfirm}
              disabled={resetting}
              className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-bold bg-red-600 text-white hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              data-testid="reset-confirm-final"
            >
              {resetting
                ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                : <Trash2 className="w-4 h-4" />}
              {resetting ? "Deleting..." : "Delete Everything"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Modal */}
      {showImport && <ImportModal onClose={() => setShowImport(false)} />}
    </div>
  );
}
